import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import type { Maybe, RxUsage } from '~/api/graphql/generated/types'
import {
  fetchPrescriptionWithOptions,
  getPrescriptionById,
} from '~/store/duck/prescriptions'
import type { PrescriptionV2 } from '~/types/entities/prescriptions'

import {
  EMPTY_FIELD_PLACEHOLDER,
  getShippingDisplayFromShipments,
  hasOrderTableLoadedData,
  hasRxUsages,
} from './rxCardUtilities'

export interface RxShipment {
  createdAt?: string | null
  deliveredAt?: string | null
  externalShipmentId?: string | null
  shipmentCarrier?: string | null
  shippedAt?: string | null
  trackingId?: string | null
  updatedAt?: string | null
}

export interface RxUsageWithShipments {
  dispensedAmount?: string | null
  externalRxUsageId?: string | null
  rxOrder?: {
    externalOrderId?: string | null
    placedAt?: string | null
    promiseDeliveryDate?: string | null
    rxShipments?: RxShipment[] | null
  } | null
  rxUsageState?: { id: string; name?: string | null } | null
}

export interface RxUsagesWithShipmentsData {
  id: string
  rxNo?: string | null
  rxUsages?: Array<RxUsageWithShipments | null> | null
}

type PrescriptionWithId =
  | {
      id: string
      rxNo?: string | null
      rxUsages?: Maybe<Array<Maybe<RxUsage>>> | RxUsageWithShipments[] | null
    }
  | null
  | undefined

function usePrescriptionForOrderTable(
  prescription: PrescriptionWithId,
  isChewy: boolean,
): {
  orderTableLoadedRx: PrescriptionV2 | undefined
  prescriptionForShipping: PrescriptionWithId
} {
  const dispatch = useDispatch()
  const storePrescription = useSelector(
    getPrescriptionById(prescription?.id ?? null),
  )
  const fetchRequestedRef = useRef<string | null>(null)

  useEffect(() => {
    if (
      !isChewy ||
      !prescription?.id ||
      fetchRequestedRef.current === prescription.id
    ) {
      return
    }
    if (storePrescription && hasRxUsages(storePrescription)) {
      return
    }
    fetchRequestedRef.current = prescription.id
    dispatch(
      fetchPrescriptionWithOptions(prescription.id, {
        includeRefills: true,
        includeRxUsages: true,
      }),
    )
  }, [dispatch, isChewy, prescription?.id, storePrescription])

  const hasLoadedData = Boolean(
    storePrescription &&
    (isChewy
      ? hasRxUsages(storePrescription)
      : hasOrderTableLoadedData(storePrescription)),
  )
  const orderTableLoadedRx = hasLoadedData
    ? (storePrescription as PrescriptionV2)
    : undefined
  const prescriptionForShipping =
    isChewy && storePrescription && hasRxUsages(storePrescription)
      ? (storePrescription as PrescriptionWithId)
      : prescription

  return { orderTableLoadedRx, prescriptionForShipping }
}

export function useRxOrderTableShipments(
  prescription: PrescriptionWithId,
  isChewy: boolean,
): {
  data: RxUsagesWithShipmentsData | null
  orderTableLoadedRx: PrescriptionV2 | undefined
} {
  const { orderTableLoadedRx, prescriptionForShipping } =
    usePrescriptionForOrderTable(prescription, isChewy)

  if (isChewy && prescriptionForShipping) {
    const rawRxUsages = prescriptionForShipping.rxUsages ?? null
    // Preserve nulls to maintain rxUsages[i] → cardHeaders[i] alignment
    const rxUsagesPreservingIndices = Array.isArray(rawRxUsages)
      ? rawRxUsages.map((usage) => usage ?? null)
      : null

    const data: RxUsagesWithShipmentsData = {
      id: prescriptionForShipping.id,
      rxNo: prescriptionForShipping.rxNo ?? null,
      rxUsages: rxUsagesPreservingIndices,
    }

    return { data, orderTableLoadedRx: orderTableLoadedRx ?? undefined }
  }

  return { data: null, orderTableLoadedRx: orderTableLoadedRx ?? undefined }
}

export function getShippingForUsage(usage: RxUsageWithShipments | null): {
  primary: string | null
  secondary: string | null
} {
  if (!usage?.rxOrder?.externalOrderId) {
    return { primary: EMPTY_FIELD_PLACEHOLDER, secondary: null }
  }
  return getShippingDisplayFromShipments(
    usage.rxOrder?.rxShipments ?? [],
    usage.rxOrder?.promiseDeliveryDate,
  )
}

export function getShippingRowTexts(
  data: RxUsagesWithShipmentsData | null,
  isVetApproved: boolean,
): (string | string[])[] {
  if (!data?.rxUsages?.length || !isVetApproved) return []

  return data.rxUsages.map((usage): string | string[] => {
    if (!usage) return EMPTY_FIELD_PLACEHOLDER
    const { primary, secondary } = getShippingForUsage(usage)
    const primaryLine = primary ?? EMPTY_FIELD_PLACEHOLDER
    return secondary ? [primaryLine, secondary] : primaryLine
  })
}
